/**
 * Report-page admin NP chat — the "Partners" aside tab.
 *
 * Lists the Network Partners who actually worked this report — locked on
 * an assignment that reached ``sent_to_backoffice`` — and opens the same
 * per-NP Freshdesk thread the triage step used (the chat service resolves
 * ``entityType=report`` up to the parent report-request anchor, so both
 * surfaces share one conversation). Shortlisted-but-never-locked NPs are
 * deliberately excluded.
 *
 * Armadillo, the registry-document supplier, is listed from its first
 * locked assignment (the automatic one its orders keep, or a manual one),
 * and its conversation is composed from the report's registry orders: the
 * research desk's emails and the replies sent through the shared mailbox,
 * never a Freshdesk thread.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Anchor,
  Badge,
  Blockquote,
  Button,
  Card,
  Group,
  Modal,
  Rating,
  Spoiler,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { ChatPanel } from '@/components/Chat';
import {
  RegistryConversation,
  unansweredCount,
} from '@/components/RegistryOrders/RegistryConversation';
import { useRegistryConversation } from '@/hooks/useRegistryConversation';
import { vnextApiClient } from '@/services/vnext-api';
import type { BackofficeAssignment } from '@/types/np-assignments';
import {
  assignmentContext,
  isRegistryPartnerEntry,
  registryPartnerOf,
  type DispatchedPartner,
} from './dispatchedPartners';

export {
  groupDispatchedPartners,
  registryPartnerOf,
  type DispatchedPartner,
} from './dispatchedPartners';

/** `initialNpId` naming Armadillo, whatever its partner id is here. */
export const REGISTRY_PARTNER_SELECTION = 'registry';

interface ReportPartnersPanelProps {
  reportId: string;
  partners: DispatchedPartner[];
  /** Called after a review is submitted so the owner can refetch. */
  onReviewed?: () => void | Promise<void>;
  /** The partner selected first: an NP id, or `"registry"` for Armadillo
   * (the Armadillo deep link). The first partner otherwise. */
  initialNpId?: string | null;
  /** What Armadillo's conversation answers first, as its deep link named
   * it. A new value selects Armadillo again and points the composer. */
  initialReplyTarget?: string | null;
}

/** The selected partner: the one picked here, else the one asked for, else
 * the first; a pick that is no longer listed is ignored. */
function activePartnerId(
  partners: readonly DispatchedPartner[],
  selected: string | null,
  initialNpId: string | null | undefined
): string | null {
  const preferred =
    initialNpId === REGISTRY_PARTNER_SELECTION
      ? (registryPartnerOf(partners)?.npId ?? null)
      : (initialNpId ?? null);
  const listed = (id: string | null) => id !== null && partners.some((p) => p.npId === id);
  if (listed(selected)) {
    return selected;
  }
  return listed(preferred) ? preferred : (partners[0]?.npId ?? null);
}

export function ReportPartnersPanel({
  reportId,
  partners,
  onReviewed,
  initialNpId,
  initialReplyTarget = null,
}: Readonly<ReportPartnersPanelProps>) {
  const [selectedNpId, setSelectedNpId] = useState<string | null>(null);
  const [reviewTarget, setReviewTarget] = useState<ReviewTarget | null>(null);
  const activeNpId = activePartnerId(partners, selectedNpId, initialNpId);
  const registryPartner = registryPartnerOf(partners);
  const registryNpId = registryPartner?.npId ?? null;
  // Armadillo's conversation is read only while Armadillo is listed: its
  // unanswered count rides the card, whichever partner is open.
  const registry = useRegistryConversation(registryPartner ? reportId : null);

  // A Reply control on the orders card names an email while another
  // partner may be open: the conversation it points at comes to the front.
  useEffect(() => {
    if (initialReplyTarget && registryNpId) {
      setSelectedNpId(registryNpId);
    }
  }, [initialReplyTarget, registryNpId]);

  if (partners.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        No Network Partners have been dispatched on this report.
      </Text>
    );
  }

  return (
    <Stack gap="sm" h="100%" style={{ minHeight: 0, flex: 1 }}>
      <Stack gap={4}>
        {partners.map((partner) => (
          <PartnerCard
            key={partner.npId}
            partner={partner}
            selected={partner.npId === activeNpId}
            toAnswer={partner === registryPartner ? unansweredCount(registry.conversation) : 0}
            onSelect={() => setSelectedNpId(partner.npId)}
            onRate={(assignment) => setReviewTarget({ partner, assignment })}
          />
        ))}
      </Stack>

      {activeNpId && activeNpId === registryPartner?.npId && (
        <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex' }}>
          <RegistryConversation
            reportId={reportId}
            conversation={registry.conversation}
            loading={registry.loading}
            error={registry.error}
            onReply={registry.reply}
            initialTarget={initialReplyTarget}
          />
        </div>
      )}
      {activeNpId && activeNpId !== registryPartner?.npId && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <ChatPanel
            entityType="report"
            entityId={reportId}
            kind="np_thread"
            npId={activeNpId}
            enableAttachments
          />
        </div>
      )}

      <ReviewModal
        target={reviewTarget}
        onClose={() => setReviewTarget(null)}
        onReviewed={async () => {
          setReviewTarget(null);
          await onReviewed?.();
        }}
      />
    </Stack>
  );
}

/** One partner: who it is, a line per assignment, the way to rate it and
 * its investigator notes. Armadillo supplies documents rather than
 * investigating, so it is never rated; its card counts the desk's emails
 * still to answer instead. */
function PartnerCard({
  partner,
  selected,
  toAnswer,
  onSelect,
  onRate,
}: Readonly<{
  partner: DispatchedPartner;
  selected: boolean;
  /** The research desk's emails waiting for an answer (Armadillo only). */
  toAnswer: number;
  onSelect: () => void;
  onRate: (assignment: BackofficeAssignment) => void;
}>) {
  const np = partner.networkPartner;
  const reviewable = isRegistryPartnerEntry(partner)
    ? undefined
    : partner.assignments.find((a) => a.completedAt && !a.review);
  return (
    <Card
      withBorder
      padding="xs"
      radius="sm"
      style={selected ? { borderColor: 'var(--mantine-color-blue-5)' } : undefined}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <UnstyledButton
          onClick={onSelect}
          style={{ minWidth: 0, flex: 1 }}
          data-testid={`report-partner-${partner.npId}`}
        >
          <Stack gap={0}>
            <Group gap={6} wrap="nowrap">
              <Text size="sm" fw={600} truncate>
                {np.name ?? np.email ?? partner.npId}
              </Text>
              {toAnswer > 0 && (
                <Badge
                  size="xs"
                  variant="light"
                  color="orange"
                  data-testid={`report-partner-to-answer-${partner.npId}`}
                >
                  {toAnswer} to answer
                </Badge>
              )}
            </Group>
            {np.company && (
              <Text size="xs" c="dimmed" truncate>
                {np.company}
              </Text>
            )}
            {partner.assignments.map((assignment) => (
              <AssignmentLine
                key={assignment.id}
                assignment={assignment}
                labels={partner.assignmentLabels}
              />
            ))}
          </Stack>
        </UnstyledButton>
        <Stack gap={4} align="flex-end">
          <Anchor
            component={Link}
            to={`/admin/network-partners/${partner.npId}`}
            size="xs"
            data-testid={`report-partner-manage-${partner.npId}`}
          >
            Manage
          </Anchor>
          {reviewable && (
            <Button
              size="compact-xs"
              variant="light"
              color="teal"
              onClick={() => onRate(reviewable)}
              data-testid={`rate-partner-${partner.npId}`}
            >
              Rate this partner
            </Button>
          )}
        </Stack>
      </Group>
      {/* NP submission notes from the Backoffice callback — kept
          outside the select button (Spoiler has its own control). */}
      {partner.assignments
        .filter((a) => a.investigatorNotes)
        .map((a) => (
          <Spoiler
            key={`notes-${a.id}`}
            maxHeight={44}
            showLabel="Show full notes"
            hideLabel="Hide notes"
            mt={6}
            styles={{ control: { fontSize: 'var(--mantine-font-size-xs)' } }}
          >
            <Blockquote
              color="gray"
              p="xs"
              cite="— investigator notes"
              data-testid={`investigator-notes-${a.id}`}
            >
              <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>
                {a.investigatorNotes}
              </Text>
            </Blockquote>
          </Spoiler>
        ))}
    </Card>
  );
}

function AssignmentLine({
  assignment,
  labels,
}: Readonly<{ assignment: BackofficeAssignment; labels: Readonly<Record<string, string>> }>) {
  return (
    <Group gap={6} wrap="nowrap">
      <Text size="xs" c="dimmed" truncate data-testid={`report-partner-context-${assignment.id}`}>
        {assignmentContext(assignment, labels)}
      </Text>
      {assignment.reworkSentAt && (
        <Badge size="xs" variant="light" color="blue">
          Rework sent {new Date(assignment.reworkSentAt).toLocaleDateString()}
        </Badge>
      )}
      {assignment.review && (
        <Badge size="xs" variant="light" color="teal">
          Rated {assignment.review.overall}
        </Badge>
      )}
    </Group>
  );
}

export interface ReviewTarget {
  partner: DispatchedPartner;
  assignment: BackofficeAssignment;
}

/** Pairs every completed-but-unreviewed assignment with its partner —
 * the queue the approve flow walks to prompt for ratings. Armadillo
 * supplies documents rather than investigating, so it is never rated. */
export function reviewablePairs(partners: DispatchedPartner[]): ReviewTarget[] {
  return partners
    .filter((partner) => !isRegistryPartnerEntry(partner))
    .flatMap((partner) =>
      partner.assignments
        .filter((a) => a.completedAt && !a.review)
        .map((assignment) => ({ partner, assignment }))
    );
}

export function ReviewModal({
  target,
  onClose,
  onReviewed,
}: Readonly<{
  target: ReviewTarget | null;
  onClose: () => void;
  onReviewed?: () => void | Promise<void>;
}>) {
  const [timeliness, setTimeliness] = useState(0);
  const [quality, setQuality] = useState(0);
  const [easeOfInteraction, setEaseOfInteraction] = useState(0);
  const [gtiExperience, setGtiExperience] = useState(0);
  const [gtiHint, setGtiHint] = useState<string | null>(null);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setTimeliness(0);
    setQuality(0);
    setEaseOfInteraction(0);
    setGtiExperience(0);
    setGtiHint(null);
    setComments('');
  };

  // "Experience with GTI" is pre-filled from the partner's completed
  // engagement count (the API owns the rule); the reviewer can adjust.
  // Best-effort: a failed lookup just leaves the row unset.
  const npId = target?.partner.npId ?? null;
  // Keyed on the assignment too: the approve-time chain can hand over
  // the same partner's next engagement, which must pre-fill afresh.
  const assignmentId = target?.assignment.id ?? null;
  useEffect(() => {
    if (!npId || !assignmentId) {
      return;
    }
    let cancelled = false;
    setGtiHint(null);
    (async () => {
      try {
        const data = await vnextApiClient.getNetworkPartnerEngagements(npId, {
          page: 1,
          pageSize: 1,
        });
        if (cancelled) {
          return;
        }
        setGtiExperience(data.gti_experience_default ?? 0);
        const count = data.completed_count;
        setGtiHint(
          data.gti_experience_default != null
            ? `Pre-filled from ${count} completed engagement${count === 1 ? '' : 's'} — adjust if needed`
            : 'No completed engagements on record yet — rate as you see fit'
        );
      } catch {
        // Pre-fill is a convenience; the reviewer can still rate by hand.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [npId, assignmentId]);

  const submit = async () => {
    if (!target) {
      return;
    }
    setSubmitting(true);
    try {
      await vnextApiClient.reviewBackofficeAssignment(target.assignment.id, {
        timeliness,
        quality,
        ease_of_interaction: easeOfInteraction,
        gti_experience: gtiExperience,
        comments: comments.trim() || null,
      });
      notifications.show({
        title: 'Review saved',
        message: 'Thanks — partner quality is computed from these reviews.',
        color: 'green',
      });
      reset();
      // Visibility is the parent's call: onReviewed either clears the
      // target (panel) or advances to the next unreviewed engagement
      // (approve-time rating chain). onClose stays a pure dismissal.
      await onReviewed?.();
    } catch (err) {
      notifications.show({
        title: 'Could not save review',
        message: err instanceof Error ? err.message : String(err),
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const npName =
    target?.partner.networkPartner.name ?? target?.partner.networkPartner.email ?? 'this partner';

  return (
    <Modal
      opened={target !== null}
      onClose={() => {
        reset();
        onClose();
      }}
      title={`Rate ${npName}`}
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          How did {npName} perform on this engagement?
        </Text>
        <Group justify="space-between">
          <Text size="sm">Timeliness</Text>
          <Rating value={timeliness} onChange={setTimeliness} data-testid="rating-timeliness" />
        </Group>
        <Group justify="space-between">
          <Text size="sm">Quality</Text>
          <Rating value={quality} onChange={setQuality} data-testid="rating-quality" />
        </Group>
        <Group justify="space-between">
          <Text size="sm">Ease of interaction</Text>
          <Rating
            value={easeOfInteraction}
            onChange={setEaseOfInteraction}
            data-testid="rating-ease-of-interaction"
          />
        </Group>
        <Group justify="space-between" align="flex-start">
          <Stack gap={0}>
            <Text size="sm">Experience with GTI</Text>
            {gtiHint && (
              <Text size="xs" c="dimmed" data-testid="rating-gti-experience-hint">
                {gtiHint}
              </Text>
            )}
          </Stack>
          <Rating
            value={gtiExperience}
            onChange={setGtiExperience}
            data-testid="rating-gti-experience"
          />
        </Group>
        <Textarea
          label="Comments"
          placeholder="Optional"
          autosize
          minRows={2}
          value={comments}
          onChange={(e) => setComments(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            color="teal"
            loading={submitting}
            disabled={
              timeliness === 0 || quality === 0 || easeOfInteraction === 0 || gtiExperience === 0
            }
            onClick={() => void submit()}
            data-testid="submit-review"
          >
            Save review
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
